// Vue core libraries - Pinia & Vue
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { DateTime } from "luxon";

// TMI - content type and query definitions
import { useApplicationStore } from "@/stores/Application";
import { usePermissionsStore } from "@/stores/Permissions";
import { normalizeBonusReport } from "@/services/data/sync/bonus-reports-sync-coordinator";
import { hydrateContent } from "@/services/data/sync/content-hydration";
import { isFreelyAvailableContent } from "@/utils/access-utils";

import { goFetch } from "@/utils/network-utils";
import { retryOnceOnServiceUnavailable } from "@/utils/service-unavailable-retry";
import { clientStateManager } from "@/services/error-services";

import type { ContentCatalogRequest } from "@/services/data/sync/content-sync";
import type { BonusReports } from "@tmi/types/api/content-models";
import { bonusReports, cache, CacheClosedError } from "@/services/cache";
import { createInitLatch } from "@/stores/init-latch";
import { createCacheReload } from "@/stores/cache-reload";
import { createLoadStatus } from "@/stores/load-status";
import {
    BonusReport,
    Category,
    ContentfulBonusReport,
    ContentfulItem,
    Countable,
    NormalizedContent,
    Product
} from "@tmi/types/entities";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
import { formatDateAsISO8601String } from "@tmi/utils/date-time";
import { useAuthStore } from "@/stores/Auth";
const logger = createDefaultLogger();

// This is implemented as a computed property to avoid unnecessarily calculating it multiple times
const bonusReportCategories = computed< Category[] >( () => {
    // Contains the full catalog of all articles the current user has access to with all dependent objects associated
    const { allBonusReports } = useBonusReportsStore();

    const categoryCountMap: Map< string, Countable< Category > > = new Map< string, Countable< Category > >();
    allBonusReports.forEach( ( item: ContentfulItem ): void => {
        item.categories.forEach( ( category: Category ): void => {
            if ( !categoryCountMap.has( category.slug ) ) {
                categoryCountMap.set( category.slug, {
                    item: category,
                    count: 0
                } );
            }

            categoryCountMap.get( category.slug )!.count++;
        } );
    } );

    const categoriesByObservationCount: Countable< Category >[] = [ ...categoryCountMap.values() ];
    const categoriesByAscendingObservationCount: Countable< Category >[] =
        categoriesByObservationCount.sort( ( container1: Countable< Category >, container2: Countable< Category > ) => container1.count - container2.count );

    return categoriesByAscendingObservationCount.map( ( container: Countable< Category > ) => container.item );
} );

// State values
const isLoading = computed< boolean >( () => loading.value );

const isInitialized = computed< boolean >( () => allBonusReports.value.length > 0 );
const loading = ref< boolean >( false );
// tmi-7be: a cache failure stops initialization until reset(), so no watcher can re-run it in a loop.
const initLatch = createInitLatch();
// data-12: whether the list load failed, so the report search says "couldn't load" rather than "No results found".
const listLoad = createLoadStatus();

// `let`: reloadFromCacheAsync() builds replacements off to the side and swaps them in with the catalog.
let allBonusReportsBySlugMap = new Map< string, BonusReport >();
let allBonusReportsByIdMap = new Map< string, BonusReport >();
const allBonusReports = ref< BonusReport[] >( [] );

export const useBonusReportsStore = defineStore( 'bonusReports', () => {
    // Depends on appStore for authentication state and to signal that data is being loaded
    const appStore = useApplicationStore();
    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );
    const permissionsStore = usePermissionsStore();

    const cacheReload = createCacheReload< NormalizedContent >( {
        binding: bonusReports,
        loading,
        latch: initLatch,
        canRead: () => isAuthenticated.value,
        apply( rows: NormalizedContent[] ): void {
            // The first row for an id or a slug wins, as it does in mergeAndSortBonusReportsCatalog.
            const bySlug = new Map< string, BonusReport >();
            const byId = new Map< string, BonusReport >();
            rows.forEach( ( row: NormalizedContent ): void => {
                const report: BonusReport = hydrateContent( row, appStore.compositionCtx );
                if ( !byId.has( report.contentfulId ) ) {
                    byId.set( report.contentfulId, report );
                }
                if ( !bySlug.has( report.slug ) ) {
                    bySlug.set( report.slug, report );
                }
            } );

            const catalog: BonusReport[] = Array.from( bySlug.values() )
                .filter( hasAccessToBonusReport )
                .sort( byPublishedDateNewestFirst );
            allBonusReportsBySlugMap = bySlug;
            allBonusReportsByIdMap = byId;
            allBonusReports.value = catalog;
            // The sync worker filled the cache after a failed load: the list is no longer "couldn't load".
            listLoad.succeed();
        },
        reinitialize: () => {
            reset();
            return initialize();
        },
        report: ( err: unknown ) => {
            logger.error( '[ BonusReports ] could not reload the cached bonus reports', err );
            clientStateManager.queueClientError( err, {} );
        }
    } );

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        // Reset the tracking for unique subscription articles
        loading.value = false;
        initLatch.clear();
        cacheReload.cancel();
        listLoad.reset();

        // Reset the aggregate collection of all articles
        allBonusReportsBySlugMap.clear();
        allBonusReportsByIdMap.clear();
        allBonusReports.value = [];
    }

    function bonusReportBySlug( slug: string ): BonusReport | undefined {
        return allBonusReportsBySlugMap.get( slug );
    }

    function bonusReportById( id: string ): BonusReport | undefined {
        return allBonusReportsByIdMap.get( id );
    }

    /**
     * Load all data that is stored within the local IndexedDB or request data from API if not present
     */
    async function initialize(): Promise< void > {
        if ( !isAuthenticated.value || isInitialized.value || loading.value || initLatch.isHalted() ) {
            return;
        }

        loading.value = true;
        listLoad.start();
        const t0: number = performance.now();

        try {
            // Wait for the database to open before the first read.
            await cache.ready();

            if ( !await bonusReports.isEmpty() ) {
                try {
                    const loadedReports: BonusReport[] = ( await bonusReports.all() )
                        .map( ( item: NormalizedContent ) => hydrateContent( item, appStore.compositionCtx ) );
                    mergeAndSortBonusReportsCatalog( loadedReports );

                    const t1: number = performance.now();
                    logger.info( `(local) Loaded ${ loadedReports.length } bonus reports in ${ t1 - t0 } ms` );
                    listLoad.succeed();
                } catch ( err: any ) {
                    // A newer build evicted this context: stop quietly, the reload is already in flight.
                    if ( err instanceof CacheClosedError ) {
                        initLatch.halt( err );
                        return;
                    }

                    logger.error( '[ BonusReports ] could not load the cached bonus reports', err );
                    initLatch.halt( err );   // a cache read failure stays latched until reset()
                    listLoad.fail( err );
                }
            } else {
                const startingDate = formatDateAsISO8601String( DateTime.fromFormat( '2020-01-01', 'yyyy-MM-dd' ).toUTC() );
                try {
                    const reports: BonusReport[] = await refreshBonusReportsFromServer( startingDate );
                    mergeAndSortBonusReportsCatalog( reports );

                    const t1: number = performance.now();
                    logger.info( `Fetching & mapping ${ reports.length } bonus reports took ${ t1 - t0 } ms` );
                    listLoad.succeed();
                } catch ( err: unknown ) {
                    // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error
                    reset();
                    clientStateManager.queueClientError( err, { startingDate } );
                    listLoad.fail( err );   // after reset(), which clears it
                }
            }
        } catch ( err: any ) {
            // A newer build evicted this context: stop quietly, the reload is already in flight.
            if ( err instanceof CacheClosedError ) {
                initLatch.halt( err );
                return;
            }

            logger.error( '[ BonusReports ] could not initialize the bonus reports', err );
            initLatch.halt( err );   // a cache open failure stays latched until reset()
            listLoad.fail( err );
        } finally {
            // Clear the "private loading" flag
            loading.value = false;
        }
    }

    /**
     * Re-reads the bonus reports the sync worker just wrote, in place: see createCacheReload. Replaces the
     * `reset()` + `initialize()` pair UPDATE_COMPLETE used, which emptied the reports list for the read.
     */
    function reloadFromCacheAsync(): Promise< void > {
        return cacheReload.reloadFromCacheAsync();
    }

    /**
     * "Try again" on a list that failed to load. An explicit retry re-arms a latched cache failure, as reset() would,
     * without emptying anything first.
     */
    function retryLoad(): Promise< void > {
        initLatch.clear();
        return initialize();
    }

    /** Rejects when the request failed: initialize() resets, reports and records the failure (listLoad). */
    async function refreshBonusReportsFromServer( latestUtcDate: string ): Promise< BonusReport[] > {
        let retrievedReports: BonusReport[] = [];
        // tmi-p00: a 503 before the server's first cache fill is retried once after its Retry-After, not shown as a failure.
        const response =
            await retryOnceOnServiceUnavailable(
                () => goFetch< BonusReports, ContentCatalogRequest >( {
                    serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                    endpoint: 'content/bonus-report-catalog',
                    payload: {
                        skip: 0,
                        limit: 250,
                        contentFilter: {
                            fromPublishedDateTimeUTC: latestUtcDate
                        }
                    },
                    authToken: authToken.value,
                    // data-12: a timeout or a dropped connection resolved null, and the list read as "No results found".
                    forceThrowExceptions: true
                } ) );

        if ( null != response?.data?.bonusReportCatalog?.bonus_reports ) {
            response.data.bonusReportCatalog.bonus_reports
                .map( ( r: ContentfulBonusReport ) => {
                    const normalized = normalizeBonusReport( r );
                    if ( normalized ) {
                        retrievedReports.push( hydrateContent( normalized, appStore.compositionCtx ) );
                    }
                } );
        }

        return retrievedReports;
    }

    function mergeAndSortBonusReportsCatalog( newBonusReports: BonusReport[] ): void {
        newBonusReports.forEach( ( newReport: BonusReport ): void => {
            if ( !allBonusReportsByIdMap.has( newReport.contentfulId ) ) {
                allBonusReportsByIdMap.set( newReport.contentfulId, newReport );
            }

            if ( !allBonusReportsBySlugMap.has( newReport.slug ) ) {
                allBonusReportsBySlugMap.set( newReport.slug, newReport );

                // Filter out any content to which the current user has not been granted access
                if ( hasAccessToBonusReport( newReport ) ) {
                    allBonusReports.value.push( newReport );
                }
            }
        } );

        allBonusReports.value.sort( byPublishedDateNewestFirst );
    }

    function hasAccessToBonusReport( report: BonusReport ): boolean {
        return report.products.some( ( p: Product ) =>
            !!( p.codes ) && ( permissionsStore.hasAccessBasedOnShortCode( p.codes ) || isFreelyAvailableContent( p.slug ) )
        );
    }

    function byPublishedDateNewestFirst( r1: BonusReport, r2: BonusReport ): number {
        return r1.publishedDate < r2.publishedDate ? 1:-1;
    }

    return {
        // ACTIONS
        bonusReportById,
        bonusReportBySlug,
        bonusReportCategories,
        initialize,
        reloadFromCacheAsync,
        reset,
        retryLoad,

        // GETTERS
        allBonusReports,
        isLoading,
        loadError: listLoad.error,
        loadFailure: listLoad.failure,
        loadStatus: listLoad.status
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useBonusReportsStore, import.meta.hot ) );
}
